import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";
import { AddOnItemName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import AuthForm from "components/Auth";
import { PAGE_VISITED } from "constants/trackingProps";
import { AuthAction } from "features/Auth/schema";
import { getParams, trackEvent } from "utils/tracking";
import { useLogin } from "utils/useLogin";

import { getRedirectUrl } from "../helpers";
import ProgramInfo from "./ProgramInfo";

interface Props {
  fromRoutes: string;
  checkOutForSplashPage: (plan: any) => void;
  planDetails: JSX.Element;
  notQualified?: boolean;
  sidebarPic?: string;
  plan?: AddOnItemName | Plans;
  buttonText?: JSX.Element;
  buttonTextBefore?: JSX.Element;
  buttonLabel?: string;
  additionalRoutes?: JSX.Element;
  backgroundColor?: string;
}

const SplashPageHome = ({
  fromRoutes,
  checkOutForSplashPage,
  planDetails,
  sidebarPic,
  plan,
  notQualified,
  buttonText,
  buttonTextBefore,
  buttonLabel,
  additionalRoutes,
  backgroundColor = "yellow.light",
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const isSignedIn = getParams("signed_in") === "true";

  const { isLoaded } = useAuth();
  const { fetchUserInfo, fetchUserLoading } = useLogin();
  const redirectUrl = getRedirectUrl(fromRoutes, location);

  const { currentOrg } = useContext(OrganizationUserContext);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else if (isSignedIn && plan) {
      checkOutForSplashPage(plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentOrg?.id]);

  return fetchUserLoading || !isLoaded ? (
    <Backdrop
      sx={{
        backgroundColor: "white",
        zIndex: 9999,
        position: "absolute",
      }}
      open={fetchUserLoading}
      appear={false}
    >
      <CircularProgress />
    </Backdrop>
  ) : (
    <Grid
      container
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        justifyContent: "center",
        backgroundColor,
      }}
    >
      <Grid container>
        {!isMobileScreen && sidebarPic && (
          <Grid
            item
            xs={4}
            sx={{
              backgroundImage: `url(${sidebarPic})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          ></Grid>
        )}

        <Grid
          item
          xs
          md
          lg
          container
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Routes>
            <Route
              path="/"
              element={
                <ProgramInfo
                  checkoutForSplashPage={checkOutForSplashPage}
                  notQualified={Boolean(notQualified)}
                  planDetails={planDetails}
                  fromRoutes={fromRoutes}
                  fetchUserLoading={fetchUserLoading}
                  plan={plan}
                  buttonTextBefore={buttonTextBefore}
                  buttonText={buttonText}
                  buttonLabel={buttonLabel}
                />
              }
            />
            <Route
              path="/sign-in/*"
              element={
                <AuthForm
                  redirectUrl={redirectUrl}
                  action={AuthAction.SIGN_IN}
                  signInUrl={`/${fromRoutes}/sign-in${location.search}`}
                  signUpUrl={`/${fromRoutes}/sign-up${location.search}`}
                  fromRoutes={fromRoutes}
                />
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <AuthForm
                  redirectUrl={redirectUrl}
                  action={AuthAction.SIGN_UP}
                  signInUrl={`/${fromRoutes}/sign-in${location.search}`}
                  signUpUrl={`/${fromRoutes}/sign-up${location.search}`}
                  fromRoutes={fromRoutes}
                />
              }
            />
            {additionalRoutes}
          </Routes>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SplashPageHome;
